/**
 * PDF.js library setup and configuration
 */

// Use legacy build for better compatibility
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'

// Re-export for convenience
export type { PDFDocumentProxy, PDFPageProxy }
export { pdfjsLib }

let isSetup = false

/**
 * The script this module was evaluated from, which can only be read while it runs: the embed
 * bundle, a classic script on a page of any origin. The app's chunks are modules, and have none.
 */
const ownScript = typeof document === 'undefined' ? null : document.currentScript

/**
 * Where this release's run-time files are (`pdfjsFiles` in `vite.config.ts`). The embed bundle asks
 * the origin it was loaded from, because the page around it may be a customer's, and every origin
 * serving the bundle routes the same `/static/`. A worker on another origin is imported through a
 * blob, which is why nginx grants that path a cross-origin read.
 */
function filesUrl(): string {
  const path = `assets/pdfjs/${pdfjsLib.version}/`
  const script = ownScript instanceof HTMLScriptElement ? ownScript.src : ''
  return script ? new URL(`/static/videas-frontend/${path}`, script).href : `${import.meta.env.BASE_URL}${path}`
}

/** What `getDocument` is told to fetch for itself: without them a document loses its non-embedded
 * fonts, its CJK text and its JPEG 2000 images, each with no more than a console warning. */
export function pdfjsDocumentFiles() {
  const files = filesUrl()
  return {
    cMapUrl: `${files}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${files}standard_fonts/`,
    wasmUrl: `${files}wasm/`,
    iccUrl: `${files}iccs/`,
  }
}

/**
 * Sets up pdf.js worker. Call this once before loading any PDF documents.
 */
export function setupPDFJS(): void {
  if (isSetup) return

  pdfjsLib.GlobalWorkerOptions.workerSrc = `${filesUrl()}pdf.worker.min.mjs`

  isSetup = true
}

/**
 * Checks if pdf.js is already setup
 */
export function isPDFJSSetup(): boolean {
  return isSetup
}

/**
 * Gets the pdf.js library version
 */
export function getPDFJSVersion(): string {
  return pdfjsLib.version
}
